import { useCallback, useEffect, useRef, useState } from "react";
import { useDataChanged } from "../useDataChanged";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconBell,
  IconChevronDown,
  IconClock,
  IconDotMenuH,
  IconPlus,
  IconPlusCircle,
  IconRefresh,
  IconSiteGlobe,
  IconTrash,
} from "./icons";
import {
  api,
  isActiveScanStatus,
  normalizeApiText,
  type Notification,
  type Project,
  type ScanListItem,
} from "../api";
import { useAppState } from "../AppStateContext";
import { useJurisdictions } from "../useJurisdictions";
import { useI18n } from "../i18n";
import { ConfirmDialog } from "./ConfirmDialog";
import { NewProjectDialog } from "./NewProjectDialog";
import { ProjectJurisdictionDialog } from "./ProjectJurisdictionDialog";
import "./Dialog.css";

export type ScanStage = { name: string; pct: number };

export function OverviewTopbar({ onRefresh }: { onRefresh?: () => void }) {
  const { reload } = useAppState();
  const { t } = useI18n();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const { notifications: items } = await api.listNotifications();
      setNotifications(items);
    } catch {
      /* ignore */
    }
  }, []);

  useDataChanged(load);

  useEffect(() => {
    const initialId = window.setTimeout(() => void load(), 0);
    const id = window.setInterval(load, 15_000);
    return () => {
      window.clearTimeout(initialId);
      window.clearInterval(id);
    };
  }, [load]);

  const unread = notifications.filter((n) => !n.is_read).length;

  // Dismiss the panel on a click anywhere outside it (or Escape). Capture phase,
  // because the project-pill menus and dialogs stop mousedown propagation.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      await reload();
      await load();
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const markAll = async () => {
    for (const n of notifications.filter((x) => !x.is_read)) {
      try {
        await api.markRead(n.notification_id);
      } catch {
        /* ignore individual failures */
      }
    }
    await load();
  };

  return (
    <div className="overview__topbar">
      <div className="connection">
        <div className="connection__item">
          <span className="connection__dot connection__dot--green" />
          {t("topbar.llms")}
        </div>
        <div className="connection__item">
          <span className="connection__dot connection__dot--green" />
          {t("topbar.connection")}
        </div>
      </div>

      <div className="overview__topbar-actions">
        <button
          type="button"
          className="topbar-icon"
          onClick={handleRefresh}
          disabled={busy}
          aria-label={t("topbar.refresh")}
          title={t("topbar.refresh")}
        >
          <IconRefresh width={18} height={18} />
        </button>
        <div className={`notif${open ? " notif--open" : ""}`} ref={notifRef}>
          <button
            type="button"
            className="topbar-icon"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("topbar.notifications")}
            title={t("topbar.notifications")}
          >
            <IconBell width={18} height={18} />
            {unread > 0 ? <span className="notif__badge">{unread}</span> : null}
          </button>
          {open ? (
            <div className="notif__panel anim-scale-in" role="dialog">
              <header className="notif__head">
                <span>{t("topbar.notifications")}</span>
                {unread > 0 ? (
                  <button type="button" onClick={markAll}>
                    {t("topbar.markAllRead")}
                  </button>
                ) : null}
              </header>
              {notifications.length === 0 ? (
                <p className="notif__empty">{t("topbar.notifEmpty")}</p>
              ) : (
                <ul className="notif__list">
                  {notifications.map((n) => (
                    <li key={n.notification_id} className={n.is_read ? "is-read" : undefined}>
                      <p className="notif__title">{n.content.title}</p>
                      {n.content.body ? <p className="notif__body">{n.content.body}</p> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export type ProjectsRowItem = { id: string; label: string; verified?: boolean };

export function ProjectsRow({
  searches,
  activeId,
  onSelect,
  onNew,
  onDelete,
  navigateOnNewScan = "/",
}: {
  searches: ProjectsRowItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  onNew?: () => void;
  onDelete?: (id: string, label: string) => void;
  navigateOnNewScan?: string | null;
}) {
  const {
    projects,
    projectScans,
    loadProjectScans,
    setViewedScanId,
    setActiveProjectId,
    startScanForProject,
    deleteProject,
  } = useAppState();
  const { t } = useI18n();
  const { options: jurisdictions } = useJurisdictions();
  const nav = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  // Project whose jurisdiction is being edited (null = dialog closed). Holds the
  // full Project so the dialog can seed the current code/name, including a
  // retired one the catalog no longer lists.
  const [jurisdictionFor, setJurisdictionFor] = useState<Project | null>(null);
  const { pathname } = useLocation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [moreOpenId, setMoreOpenId] = useState<string | null>(null);
  const [startError, setStartError] = useState<{ projectId: string; message: string } | null>(null);
  // Project id whose scan is currently being started — disables its "+" button
  // and gives instant feedback so impatient clicks can't launch extra scans.
  const [startingId, setStartingId] = useState<string | null>(null);
  const [busyAlert, setBusyAlert] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; label: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Centralised delete handler — pages can opt out by passing onDelete, but
  // by default the row owns the confirm dialog so the "⋯" menu works on
  // every page (overview / findings / hypoteses / etc.) without per-page wiring.
  const requestDelete = (id: string, label: string) => {
    if (onDelete) {
      onDelete(id, label);
      return;
    }
    setDeleteError(null);
    setConfirmDelete({ id, label });
  };

  const isNonScanPage = pathname === "/onboarding" || pathname === "/welcome";

  useEffect(() => {
    if (!openId && !moreOpenId) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenId(null);
        setMoreOpenId(null);
        setStartError(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openId, moreOpenId]);

  const toggle = (id: string) => {
    setStartError(null);
    setOpenId((prev) => {
      const next = prev === id ? null : id;
      if (next && projectScans[id] === undefined) void loadProjectScans(id);
      return next;
    });
  };

  const handleSelectScan = (projectId: string, scanId: string) => {
    setActiveProjectId(projectId);
    setViewedScanId(scanId);
    setOpenId(null);
    if (isNonScanPage) nav("/");
  };

  const handlePillClick = (item: ProjectsRowItem) => {
    onSelect?.(item.id);
    setActiveProjectId(item.id);
    if (item.verified === false) {
      if (pathname !== "/onboarding") nav("/onboarding");
      setOpenId(null);
      return;
    }
    toggle(item.id);
  };

  const handleStartNewScan = async (projectId: string, hasActiveScan: boolean) => {
    // Ignore a repeat click while a start is already in flight (this project or
    // another) — startScanForProject also guards, but bailing here keeps the
    // button feedback honest and avoids a redundant request.
    if (startingId) return;
    setStartError(null);
    if (hasActiveScan) {
      setBusyAlert(t("projects.scanRunningBody"));
      return;
    }
    setStartingId(projectId);
    try {
      const fresh = await startScanForProject(projectId);
      setOpenId(null);
      if (fresh && navigateOnNewScan) nav(navigateOnNewScan);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("projects.scanStartFailed");
      // Backend race: another tab/user may have started a scan after we
      // rendered. Treat that the same as the disabled state.
      if (/scan[_\s-]?already[_\s-]?running/i.test(message)) {
        void loadProjectScans(projectId);
        setBusyAlert(t("projects.scanRunningBody"));
        return;
      }
      setStartError({ projectId, message });
    } finally {
      setStartingId(null);
    }
  };

  return (
    <div className="projects anim-stagger" style={{ ["--anim-stagger-step" as never]: "50ms" }} ref={containerRef}>
      <span className="projects__label">{t("projects.label")}</span>
      {searches.map((item) => {
        const isActive = item.id === activeId;
        const isOpen = openId === item.id;
        const scansForProject = projectScans[item.id];
        const hasActiveScan = Boolean(
          scansForProject?.some((s) => isActiveScanStatus(s.status)),
        );
        const isStarting = startingId === item.id;
        const isMoreOpen = moreOpenId === item.id;
        return (
          <div
            key={item.id}
            data-tour={isActive ? "project-pill" : undefined}
            className={`project-pill${isActive ? " project-pill--active" : ""}${
              isOpen ? " project-pill--open" : ""
            }`}
          >
            <button
              type="button"
              className="project-pill__main"
              onClick={() => handlePillClick(item)}
              aria-expanded={isOpen}
            >
              <span className="project-pill__left">
                <Globe />
                <span className="project-pill__name">{item.label}</span>
              </span>
            </button>
            <div className="project-pill__icons">
              {item.verified !== false ? (
                <>
                  <button
                    type="button"
                    className="project-pill__icon-btn"
                    onClick={() => handlePillClick(item)}
                    aria-label={isOpen ? t("common.close") : t("projects.openScans")}
                    aria-expanded={isOpen}
                  >
                    <IconChevronDown
                      width={20}
                      height={20}
                      className={`project-pill__chev${isOpen ? " project-pill__chev--open" : ""}`}
                    />
                  </button>
                  <button
                    type="button"
                    className={`project-pill__icon-btn${
                      hasActiveScan || isStarting ? " project-pill__icon-btn--disabled" : ""
                    }`}
                    onClick={() => handleStartNewScan(item.id, hasActiveScan)}
                    aria-disabled={hasActiveScan || isStarting}
                    aria-busy={isStarting}
                    aria-label={t("projects.newScan")}
                    title={hasActiveScan ? t("projects.scanRunningTitle") : t("projects.newScan")}
                  >
                    {hasActiveScan || isStarting ? (
                      <IconClock width={20} height={20} />
                    ) : (
                      <IconPlusCircle width={20} height={20} />
                    )}
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className={`project-pill__icon-btn${
                  isMoreOpen ? " project-pill__icon-btn--active" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpenId((cur) => (cur === item.id ? null : item.id));
                }}
                aria-haspopup="menu"
                aria-expanded={isMoreOpen}
                aria-label={t("projects.moreActions")}
                title={t("projects.moreActions")}
              >
                <IconDotMenuH width={20} height={20} />
              </button>
            </div>
            {isMoreOpen ? (
              <div
                className="project-pill__more anim-scale-in"
                role="menu"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {jurisdictions.length > 0 ? (
                  <button
                    type="button"
                    className="project-pill__more-item"
                    onClick={() => {
                      setMoreOpenId(null);
                      const project = projects.find((p) => p.project_id === item.id);
                      if (project) setJurisdictionFor(project);
                    }}
                  >
                    <IconSiteGlobe width={16} height={16} />
                    <span>{t("jurisdiction.menuItem")}</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="project-pill__more-item project-pill__more-item--danger"
                  onClick={() => {
                    setMoreOpenId(null);
                    requestDelete(item.id, item.label);
                  }}
                >
                  <IconTrash width={16} height={16} />
                  <span>{t("projects.deleteProject")}</span>
                </button>
              </div>
            ) : null}
            {isOpen ? (
              <div className="project-pill__menu anim-scale-in" role="menu" onMouseDown={(e) => e.stopPropagation()}>
                {startError && startError.projectId === item.id ? (
                  <p className="project-pill__menu-error">{startError.message}</p>
                ) : null}
                <ScanList
                  scans={scansForProject}
                  onSelect={(id) => handleSelectScan(item.id, id)}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      <button
        type="button"
        className="project-pill project-pill--new"
        // Pages that don't pass `onNew` (Hypoteses, Reports, …) used to render a
        // dead button. Fall back to the local new-project dialog so "New project"
        // works from every tab, not only after the first project exists.
        onClick={onNew ?? (() => setNewOpen(true))}
      >
        <span>{t("projects.newProject")}</span>
        <IconPlus width={16} height={16} />
      </button>
      {busyAlert ? (
        <ScanBusyDialog message={busyAlert} onClose={() => setBusyAlert(null)} />
      ) : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        title={t("projects.confirmDeleteTitle")}
        body={
          deleteError ??
          t("projects.confirmDeleteBody", { name: confirmDelete?.label ?? "" })
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteProject(confirmDelete.id);
            setConfirmDelete(null);
            setDeleteError(null);
          } catch (err) {
            setDeleteError(
              err instanceof Error ? err.message : t("projects.deleteFailed"),
            );
          }
        }}
        onClose={() => {
          setConfirmDelete(null);
          setDeleteError(null);
        }}
      />
      <NewProjectDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(project) => {
          setNewOpen(false);
          setActiveProjectId(project.project_id);
          nav("/onboarding");
        }}
      />
      {jurisdictionFor ? (
        <ProjectJurisdictionDialog
          project={jurisdictionFor}
          onClose={() => setJurisdictionFor(null)}
        />
      ) : null}
    </div>
  );
}

function ScanBusyDialog({ message, onClose }: { message: string; onClose: () => void }) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portal to <body>: this dialog is rendered from inside OverviewTopbar, whose
  // ancestor (.overview.anim-page) carries a page transform — that makes it the
  // containing block for `position: fixed`, so the backdrop's `inset: 0` fills
  // the content area below the topbar instead of the viewport and the dialog
  // centres low. Rendering at the document root fixes it (same as ConfirmDialog).
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h2>{t("projects.scanRunningTitle")}</h2>
          <button type="button" aria-label={t("common.close")} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="dialog__body">{message}</div>
        <div className="dialog__actions" style={{ padding: "0 20px 20px" }}>
          <button type="button" className="btn--auto btn--accent" onClick={onClose}>
            {t("projects.gotIt")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Back-compat alias — old callers can keep importing SearchesRow.
export const SearchesRow = ProjectsRow;

function ScanList({
  scans,
  onSelect,
}: {
  scans: ScanListItem[] | undefined;
  onSelect: (scanId: string) => void;
}) {
  const { t } = useI18n();
  if (scans === undefined) {
    return <div className="project-pill__menu-empty">{t("common.loading")}</div>;
  }
  if (scans.length === 0) {
    return <div className="project-pill__menu-empty">{t("projects.noScans")}</div>;
  }
  return (
    <div className="project-pill__menu-list">
      {scans.map((s) => (
        <button
          type="button"
          key={s.scan_id}
          className="project-pill__menu-row"
          onClick={() => onSelect(s.scan_id)}
        >
          <span className="project-pill__menu-date">{formatScanDate(s.created_at)}</span>
          <span className={`scan-badge ${badgeClass(s.status)}`}>{badgeLabel(s.status, t)}</span>
        </button>
      ))}
    </div>
  );
}

function formatScanDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
}

function badgeClass(status: string): string {
  const s = normalizeApiText(status);
  if (s === "running" || s === "paused") return "scan-badge--running";
  if (s === "completed" || s === "ready") return "scan-badge--ready";
  if (s === "cancelled" || s === "failed" || s === "error") return "scan-badge--cancelled";
  return "scan-badge--ready";
}

function badgeLabel(status: string, t: (key: import("../i18n").TranslationKey) => string): string {
  const s = normalizeApiText(status);
  if (s === "running") return t("projects.statusRunning");
  if (s === "paused") return t("projects.statusPaused");
  if (s === "completed") return t("projects.statusReady");
  if (s === "cancelled") return t("projects.statusCancelled");
  if (s === "failed" || s === "error") return t("projects.statusFailed");
  return s || "—";
}

function Globe() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <polygon
        points="12,3 20,7.5 20,16.5 12,21 4,16.5 4,7.5"
        strokeWidth={1.4}
      />
      <line x1="12" y1="3" x2="12" y2="21" strokeWidth={1} opacity={0.7} />
      <line x1="4" y1="7.5" x2="20" y2="16.5" strokeWidth={1} opacity={0.7} />
      <line x1="4" y1="16.5" x2="20" y2="7.5" strokeWidth={1} opacity={0.7} />
    </svg>
  );
}
